import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useStoreActions } from '~/store/hooks';
import { setNoon } from '~/utils/date/date';
import { durationToMinutes } from '~/utils/date/duration';
import { extractErrorCode, extractErrorMessage } from '~/utils/error/error';
import repairDollars from '~/utils/text/repairDollars';
import { useApiMutation } from '~/hooks/api';
import { ICeEntry, ICeMonthRequest } from '~/services/api/communityEngagement/types';
import { ReactComponent as ClockIcon } from '~/ui/assets/images/clockIcon.svg';
import CeErrorCode from '~/ui/pages/Clients/reusable/CommunityEngagement/constants/CeErrorCode';
import Button from '~/ui/components/common/Button';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import Modal from '~/ui/components/common/Modal';
import DateRangePicker from '~/ui/components/inputs/DateRangePicker';
import DurationInput from '~/ui/components/inputs/DurationInput';
import Input from '~/ui/components/inputs/Input';
import Textarea from '~/ui/components/inputs/Textarea';

import { getEntryDateWindow } from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/entryDateWindow';
import {
  getDefaultValues,
  getEntryMaxHours,
  getEntryResolver,
  IEntryFormValues,
  isEntryFormChanged,
  toAmount,
  toEntryDates,
} from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/entryForm';
import useCeDocuments from '~/ui/pages/Clients/reusable/CommunityEngagement/hooks/useCeDocuments';
import CeDocumentsField from '../CeDocumentsField';

import styles from '~/ui/pages/Clients/reusable/CommunityEngagement/CeModal.module.scss';

interface IEntryModalProps {
  month: ICeMonthRequest;
  /** `null` adds a new entry. */
  entry: ICeEntry | null;
  onClose: () => void;
  onSaved: () => void;
}

const EntryModal = ({ month, entry, onClose, onSaved }: IEntryModalProps): ReactElement => {
  const title = entry ? 'Edit Entry' : 'Add Entry';
  const dateWindow = getEntryDateWindow(new Date(), month);
  const defaultValues = getDefaultValues(entry);
  const resolver = getEntryResolver(month, dateWindow);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

  const { onCreateEntry, onUpdateEntry } = useStoreActions(actions => actions.communityEngagement);
  const showError = useStoreActions(actions => actions.snackbar.showError);
  const documents = useCeDocuments(entry?.documents ?? []);

  const {
    control,
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<IEntryFormValues>({
    defaultValues,
    resolver,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { mutate: save, isLoading: isSaving } = useApiMutation(
    ({ dateRange, minutesWorked, currencyEarned, comment }: IEntryFormValues) => {
      const common = {
        ...toEntryDates(dateRange),
        minutesWorked: durationToMinutes(minutesWorked),
        currencyEarned: toAmount(currencyEarned),
        comment: comment.trim() || null,
      };
      return entry
        ? onUpdateEntry({
            ...month,
            entryId: entry.id,
            entry: { ...common, documents: documents.toUpdateRequest() },
          })
        : onCreateEntry({ ...month, entry: { ...common, documents: documents.toCreateRequest() } });
    },
    {
      notifyMessage: entry ? 'Entry updated' : 'Entry added',
      throwOnError: false,
      onSuccess: () => {
        onSaved();
        onClose();
      },
      onError: error => {
        const code = extractErrorCode(error);
        if (
          code === CeErrorCode.EngagementDateOutOfRange ||
          code === CeErrorCode.EntryMonthChanged
        ) {
          setError('dateRange', { type: 'server', message: extractErrorMessage(error) });
        } else {
          showError(extractErrorMessage(error));
        }
      },
    },
  );

  const values = watch();
  const isDirty = isEntryFormChanged(values, defaultValues) || documents.isDirty;
  const hasErrors = Object.keys(errors).length > 0;

  const keepDollars = (value: string): void => {
    const repaired = repairDollars(value);
    if (repaired === value) return;
    setValue('currencyEarned', repaired, { shouldValidate: isSubmitted });
  };

  const requestClose = () => {
    if (isDirty) setIsConfirmingDiscard(true);
    else onClose();
  };

  return (
    <>
      <Modal
        open
        preventOutsideClick
        maxWidth="sm"
        fullWidth
        className={styles.paper}
        onClose={requestClose}
      >
        <section className={styles.container}>
          <header className={styles.header}>
            <IconButton aria-label="Close" className={styles.close} onClick={requestClose}>
              <CloseIcon />
            </IconButton>
            <h2 className={styles.title}>{title}</h2>
          </header>
          <form className={styles.form} onSubmit={handleSubmit(save)}>
            <div className={styles.content}>
              <DateRangePicker
                name="dateRange"
                label="Engagement Date Range"
                deps={['minutesWorked']}
                minDate={setNoon(dateWindow.min)}
                maxDate={setNoon(dateWindow.max)}
                control={control}
                errors={errors}
                hideOnSelect
                showSingleDate
              />
              <DurationInput
                control={control}
                name="minutesWorked"
                label="Hours Worked"
                maxHours={getEntryMaxHours(values.dateRange)}
                endAdornment={<ClockIcon />}
              />
              <Input
                name="currencyEarned"
                label="Dollars Earned"
                placeholder="Enter amount"
                inputMode="decimal"
                onChangeCallback={keepDollars}
                tabIndex={0}
                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                register={register}
                errors={errors}
              />
              <Textarea
                name="comment"
                label="Process Comments"
                placeholder="Add Comments"
                register={register}
                errors={errors}
              />
              <CeDocumentsField documents={documents} />
            </div>
            <footer className={styles.actions}>
              <Button type="button" color="primary" variant="outlined" onClick={requestClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSaving || documents.isBusy || (entry && !isDirty) || hasErrors}
                isLoading={isSaving}
              >
                Save
              </Button>
            </footer>
          </form>
        </section>
      </Modal>
      {isConfirmingDiscard && (
        <ConfirmModal
          description={`Are you sure you want to cancel “${title}”? All the changes will be discarded.`}
          onClose={() => setIsConfirmingDiscard(false)}
          onConfirm={onClose}
        />
      )}
    </>
  );
};

export default EntryModal;
